import {
  Avatar,
  Badge,
  Box,
  Center,
  Flex,
  SkeletonText,
  Spinner,
  Table,
  useDisclosure,
  Text
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { z } from "zod"

import { type UserPrivate, UsersOrderBy } from "../../client"
import ActionsMenu from "../../components/Common/ActionsMenu"
import Navbar from "../../components/Common/Navbar"
import { PaginationFooter } from "../../components/Common/PaginationFooter"
import { lazy, Suspense, useEffect, useMemo, useState } from "react"
import TabsList from "../../components/Common/TabsList"
import Sort from "../../components/Common/Sort"
import { userHasPermission } from "../../utils"
import { NoPermission } from "../../components/UI/no-permission"
import { readUsers } from "../../api/users"

const AddUser = lazy(() => import("../../components/Admin/AddUser"))
const DetailUser = lazy(() => import("../../components/Admin/DetailUser"))
const PER_PAGE = 25

const usersSearchSchema = z.object({
  page: z.number().catch(1),
})

export const Route = createFileRoute("/_layout/users")({
  component: Admin,
  validateSearch: (search) => usersSearchSchema.parse(search),
})


function UsersTable() {
  // Permission check
  const [us_create, us_read, us_update, us_delete] = useMemo(() => {
    return ["create", "read", "update", "delete"].map(
      (action) => userHasPermission([{ perm: `users_${action}`, is_allowed: false }])[0].is_allowed
    )
  }, [userHasPermission])

  if(!us_read) return <NoPermission/>

  const fields = [
    { title: "Name", col: "lastname", align: undefined },
    { title: "Email", col: "email", align: undefined },
    { title: "Status", col: "is_active", align: undefined },
    { title: "Aktionen", col: null, align: "right" as const },
  ]

  const [searchValue, setSearchValue] = useState<string>("")

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value)
  }

  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setPage = (page: number) =>
    navigate({
      search: (prev: { [key: string]: string }) => ({ ...prev, page }),
    })

  // Current User
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPrivate>(["currentUser"])

  // Set page on search value change
  const [prevPage, setPrevPage] = useState<number>(page)
  useEffect(() => {
    if(searchValue == "") {
      setPage(prevPage)
    } else {
      setPrevPage(page)
      setPage(1)
    }
  }, [searchValue])

  // Sorting
  const [columnName, setColumnName] = useState<UsersOrderBy>()
  const [orderBy, setOrderBy] = useState<string>("")
  const [isDesc, setIsDesc] = useState<boolean>()

  // Query
  const queryOptions = useMemo(() =>
    readUsers({
      per_page: PER_PAGE,
      page: page,
      order_by: columnName,
      descending: isDesc,
      query: searchValue,
    }),
  [page, columnName, isDesc, searchValue])

  const {
    data: users,
    isPending,
    isPlaceholderData,
  } = useQuery({
    ...queryOptions,
    placeholderData: (prevData) => prevData,
  })

  // Sorting
  const sortTable = (name: UsersOrderBy) => {
    if (columnName !== name) {
      setColumnName(name)
      setOrderBy("")
      sorting("")
    } else {
      sorting(orderBy)
    }
  }

  const sorting = (order: string) => {
    switch (order) {
      case "":
        setOrderBy("desc")
        setIsDesc(true)
        break
      case "desc":
        setOrderBy("asc")
        setIsDesc(false)
        break
      case "asc":
        setOrderBy("")
        setIsDesc(undefined)
        setColumnName(undefined)
        break
    }
  }

  const detailModal = useDisclosure()
  const [detailUser, setdetailUser] = useState<UserPrivate | null>(null)

  const openModal = (user: UserPrivate) => {
    setdetailUser(user)
    detailModal.onOpen()
  }

  return (
    <>
      <Navbar
        type="Benutzer"
        addAllowed={us_create}
        addModalAs={AddUser}
        showSearch={true}
        onSearchChange={handleSearchValueChange}
      />

      {detailModal.open &&
        <Suspense fallback={
          <Center
            bg="rgba(0, 0, 0, 0.3)"
            position="fixed"
            top="0"
            left="0"
            width="100vw"
            height="100vh"
            zIndex="10000"
          >
            <Spinner size="lg" color="white" />
          </Center>}
        >
          <DetailUser
            isOpen={detailModal.open}
            onClose={detailModal.onClose}
            user={detailUser}
          />
        </Suspense>
      }

      <Table.ScrollArea asChild>
        <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={{md: "table"}}>
          <Table.Header>
            <Table.Row>
              {fields.map((field, i) =>
                field.col != null ? (
                  <Table.ColumnHeader
                    key={i}
                    onClick={() => sortTable(field.col as UsersOrderBy)}
                    cursor="pointer"
                    textAlign={field.align}
                  >
                    {field.title}
                    {columnName === field.col ? (
                      <Sort
                        columnNamePassed={columnName}
                        orderByPassed={orderBy}
                      />
                    ) : null}
                  </Table.ColumnHeader>
                ) : (
                  <Table.ColumnHeader key={field.title} textAlign={field.align}>
                    {field.title}
                  </Table.ColumnHeader>
                )
              )}
            </Table.Row>
          </Table.Header>
          {isPending ? (
            <Table.Body>
              <Table.Row>
                {new Array(4).fill(null).map((_, index) => (
                  <Table.Cell key={index}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          ) : (
            <Table.Body>
              {!users?.data ? (
                <Table.Row>
                  <Table.Cell colSpan={4} textAlign="center">
                    Keine Benutzer verfügbar
                  </Table.Cell>
                </Table.Row>
              ) : (
                users?.data.map((user) => (
                  <Table.Row key={user.id} opacity={isPlaceholderData ? 0.5 : 1}>
                    <Table.Cell
                      color={!user.lastname ? "dim" : "inherit"}
                      onClick={() => openModal(user)}
                      cursor="pointer"
                    >
                      <Flex alignItems="center">
                        <Avatar.Root mr={2}>
                          <Avatar.Fallback
                            name={
                              user.lastname && user.firstname
                                ? `${user.lastname} ${user.firstname}`
                                : user.firstname || user.lastname || user.email
                            }
                          />
                          <Avatar.Image src={user.fk_media_profile_pic?.path.replace(/-(LG)\./, "-SM.")} />
                        </Avatar.Root>
                        <Text>
                          {user.lastname && user.firstname 
                            ? `${user.lastname} ${user.firstname}`
                            : user.firstname || user.lastname || "N/A"
                          }
                        </Text>
                        {currentUser?.id === user.id && (
                          <Badge ml="1" colorPalette="teal">
                            Du
                          </Badge>
                        )}
                      </Flex>
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(user)} cursor="pointer">
                      {user.email}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(user)} cursor="pointer">
                      <Flex gap={2}>
                        <Box
                          w="2"
                          h="2"
                          borderRadius="50%"
                          bg={user.is_active ? "success" : "danger"}
                          alignSelf="center"
                        />
                        {user.is_active ? "Aktiv" : "Inaktiv"}
                      </Flex>
                    </Table.Cell>

                    <Table.Cell  textAlign="right">
                      <ActionsMenu
                        type="Benutzer"
                        value={user}
                        disableDelete={currentUser?.id === user.id || !us_delete}
                        disableEdit={currentUser?.id === user.id || !us_update}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          )}
        </Table.Root>
      </Table.ScrollArea>

      <PaginationFooter
        onChangePage={setPage}
        page={page}
        count={users?.count ? users?.count : 0}
        perPage={PER_PAGE}
      />
    </>
  )
}

export default UsersTable

function Admin() {
  const tabs = useMemo(() => [
    {value: "users", title: "Benutzer"},
    {value: "roles", title: "Rollen"},
    {value: "permissions", title: "Berechtigungen"},
  ], [])

  return (
    <TabsList tabs={tabs} defaultVal="users" />
  )
}
